import { graphql } from 'react-relay';
import { useMemo } from 'react';
import type { WatchQueryFetchPolicy } from 'apollo-client';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { usePageContentId } from '@confluence/page-context';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { NoNetworkError } from '@confluence/network';

import type {
	usePageInfoPageInfoQuery as usePageInfoPageInfoQuery$data,
	usePageInfoPageInfoQueryVariables as usePageInfoPageInfoQuery$variables,
} from './__types__/usePageInfoPageInfoQuery.ts';
import type {
	PageInfoQuery_content_nodes_operations as PageInfoOperations,
	PageInfoQuery_content_nodes as PageInfoNode,
} from './__types__/PageInfoQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const usePageInfoPageInfoQuery = graphql`
	query usePageInfoPageInfoQuery($pageId: ID) {
		content(id: $pageId, status: ["current", "archived", "draft"]) {
			nodes {
				id
				type
				status
				space {
					id
					operations {
						operation
						targetType
					}
				}
				operations {
					operation
					targetType
				}
				version {
					number
				}
			}
		}
	}
`;
export const EMPTY_RESULT = {
	id: '',
	type: '',
	status: '',
	space: {
		id: '',
		operations: [] as PageInfoOperations[],
	},
	operations: [] as PageInfoOperations[],
	version: {
		number: -1,
	},
};

export const usePageInfo = ({
	fetchPolicy = 'cache-and-network',
	onCompleted,
	onError,
}: {
	fetchPolicy?: WatchQueryFetchPolicy;
	onCompleted?: (data: usePageInfoPageInfoQuery$data) => void;
	onError?: (error: Error) => void;
} = {}) => {
	const [contentId] = usePageContentId();
	const { data, loading, error, refetch } = useQueryInterop<
		usePageInfoPageInfoQuery$data,
		usePageInfoPageInfoQuery$variables
	>(usePageInfoPageInfoQuery, {
		variables: { pageId: contentId },
		fetchPolicy,
		onCompleted,
	});
	return useMemo(() => {
		// Handle error case first
		if (error) {
			if (isUnauthorizedError(error) || error instanceof NoNetworkError) {
				markErrorAsHandled(error);
			}
			onError?.(error);
			return { pageInfo: null as PageInfoNode | null, loading: false, error, refetch: null };
		}

		// If we have data, use it even during loading
		if (data) {
			const pageInfoNode = data.content?.nodes?.[0] ?? null;
			if (pageInfoNode) {
				return {
					pageInfo: pageInfoNode as PageInfoNode,
					loading,
					error: undefined,
					refetch,
				};
			}
			return {
				pageInfo: { ...EMPTY_RESULT } as PageInfoNode,
				loading,
				error: undefined,
				refetch,
			};
		}

		// Only return null pageInfo if we have no data at all
		return { pageInfo: null as PageInfoNode | null, loading, error: undefined, refetch: null };
	}, [data, loading, error, onError, refetch]);
};
